/**
 * TaskListKpiCards - Cards de KPI totalizadores para listagem de tarefas
 * 
 * Exibe 6 métricas: Total, Criadas Hoje, Pendentes, Em Andamento, Pausadas, Concluídas
 * Cards clicáveis que filtram a listagem por status (toggle)
 * Cores conforme imagem de referência do módulo de tarefas
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-08 v1.0.0 - Criação inicial
 */

import { ListChecks, CalendarPlus, PlayCircle, PauseCircle, CheckCircle2, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { TaskListKpis } from '@/hooks/useTaskListKpis';

/** Props do componente */
interface TaskListKpiCardsProps {
  /** Dados dos KPIs */
  kpis: TaskListKpis;
  /** Se está carregando */
  loading?: boolean;
  /** Status atualmente filtrado (para highlight do card ativo) */
  activeStatus?: string;
  /** Callback ao clicar em um card para filtrar por status */
  onFilterByStatus: (status: string | null) => void;
}

/** Definição de cada card KPI */
interface KpiCardDef {
  /** Chave do status para filtro (null = total, sem filtro direto) */
  statusKey: string | null;
  /** Título exibido */
  title: string;
  /** Campo do KPI */
  field: keyof TaskListKpis;
  /** Ícone Lucide */
  icon: typeof ListChecks;
  /** Cor do ícone (mantém cor semântica) */
  iconColor: string;
  /** Cor de fundo do card inteiro */
  cardBg: string;
  /** Cor do ring quando ativo */
  ringColor: string;
}

/** Configuração dos 6 cards conforme cores da imagem de referência */
const KPI_CARDS: KpiCardDef[] = [
  {
    statusKey: null,
    title: 'Total de Tarefas',
    field: 'total_tasks',
    icon: ListChecks,
    iconColor: 'text-purple-700 dark:text-purple-300',
    cardBg: 'bg-purple-100 dark:bg-purple-900/30',
    ringColor: 'ring-purple-400',
  },
  {
    statusKey: 'created_today',
    title: 'Criadas Hoje',
    field: 'created_today',
    icon: CalendarPlus,
    iconColor: 'text-cyan-700 dark:text-cyan-300',
    cardBg: 'bg-cyan-100 dark:bg-cyan-900/30',
    ringColor: 'ring-cyan-400',
  },
  {
    statusKey: 'pending',
    title: 'Pendentes',
    field: 'pending',
    icon: Clock,
    iconColor: 'text-slate-700 dark:text-slate-300',
    cardBg: 'bg-slate-100 dark:bg-slate-900/30',
    ringColor: 'ring-slate-400',
  },
  {
    statusKey: 'in_progress',
    title: 'Andamento',
    field: 'in_progress',
    icon: PlayCircle,
    iconColor: 'text-blue-700 dark:text-blue-300',
    cardBg: 'bg-blue-100 dark:bg-blue-900/30',
    ringColor: 'ring-blue-400',
  },
  {
    statusKey: 'paused',
    title: 'Pausadas',
    field: 'paused',
    icon: PauseCircle,
    iconColor: 'text-pink-700 dark:text-pink-300',
    cardBg: 'bg-pink-100 dark:bg-pink-900/30',
    ringColor: 'ring-pink-400',
  },
  {
    statusKey: 'completed',
    title: 'Concluídas',
    field: 'completed',
    icon: CheckCircle2,
    iconColor: 'text-green-700 dark:text-green-300',
    cardBg: 'bg-lime-100 dark:bg-green-900/30',
    ringColor: 'ring-green-400',
  },
];

export function TaskListKpiCards({ kpis, loading, activeStatus, onFilterByStatus }: TaskListKpiCardsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 px-4 py-3">
      {KPI_CARDS.map((card) => {
        /** Verifica se este card está ativo (filtro selecionado) */
        const isActive = activeStatus === card.statusKey;

        return (
          <Card
            key={card.field}
            className={cn(
              'border-0 shadow-none cursor-pointer transition-all hover:shadow-md',
              card.cardBg,
              isActive && `ring-2 ${card.ringColor} shadow-md`
            )}
            onClick={() => {
              // Toggle: se já está ativo, limpa o filtro
              if (isActive) {
                onFilterByStatus(null);
              } else {
                onFilterByStatus(card.statusKey);
              }
            }}
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                {/* Ícone + título */}
                <div className="flex items-center gap-2 min-w-0">
                  <card.icon className={`h-5 w-5 shrink-0 ${card.iconColor}`} />
                  <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">
                    {card.title}
                  </p>
                </div>
                {/* Valor */}
                {loading ? (
                  <div className="h-6 w-8 bg-white/40 dark:bg-white/10 animate-pulse rounded" />
                ) : (
                  <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
                    {kpis[card.field]}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
